import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { IoIosArrowRoundBack } from "react-icons/io";
import QuestionSection from "../../ui/QuestionSection/QuestionSection";
import CommonFooter from "../../CommonFooter/CommonFooter";
import CommonButton from "../../ui/CommonButton/CommonButton";
import classes from "./ProcessRequirements.module.css";
import { env } from "../../../constants/env";
import axios from "axios";

const ProcessRequirements = () => {
  const { labId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [currentSet, setCurrentSet] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isfetching, setIsFetching] = useState(false);

  const userRole = user?.role;
  const [editable, setEditable] = useState(userRole === "ASSESSOR");

  const handleAnswerChange = (questionId, score, comment, points) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { score, comment, points },
    }));
  };

  useEffect(() => {
    if (!labId) return;
    fetchAccreditation();
  }, [labId, token, user._id]);

  const fetchAccreditation = async () => {
    setIsFetching(true);
    try {
      const res = await axios.get(`${env.kataApiUrl}/api/accreditation`, {
        params: {
          labId,
          section: "Process Requirements",
          name: "SADCAS F134-B",
        },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data.success && res.data.accreditation) {
        const accreditation = res.data.accreditation;

        // Only assigned auditor or ASSESSOR can edit
        if (accreditation.auditorId !== user._id && userRole !== "ASSESSOR") {
          setEditable(false);
        }

        if (accreditation.answers && accreditation.answers.length > 0) {
          const mapped = accreditation.answers.reduce((acc, a) => {
            acc[a.questionId] = {
              score: a.score || "",
              comment: a.comment || "",
              points: a.points || 0,
            };
            return acc;
          }, {});
          setAnswers(mapped);
          setEditable(false); // answers exist → read-only
        } else {
          setEditable(true); // no answers → editable
        }
      }
    } catch (err) {
      console.error("Failed to fetch accreditation:", err);
    } finally {
      setIsFetching(false);
    }
  };

  const questionSets = [
    {
      questions1: [
        {
          questionId: "Q1",
          content:
            "a. Does the laboratory identify potential risks to patient care in the pre-examination, examination and post-examination processes?",
          number: "7.1",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q2",
          content:
            "b. Are the identified risks and effectiveness of the mitigation processes monitored and evaluated according to the potential harm to the patient?",
          number: "",
          multiline: true,
          hideOptions: true,
          placeholder: "Add comment",
        },
      ],
      render: (onAnswerChange, answers) => (
        <QuestionSection
          topQuestion={false}
          addWidth={true}
          questions={questionSets[0].questions1}
          onAnswerChange={editable ? onAnswerChange : () => {}}
          answers={answers}
          disabled={!editable}
        />
      ),
    },
  ];

  const handleNext = () => {
    if (currentSet < questionSets.length - 1) setCurrentSet((prev) => prev + 1);
  };

  const handlePrevious = () => {
    if (currentSet > 0) setCurrentSet((prev) => prev - 1);
  };

  const handleDone = async () => {
    setIsSubmitting(true);
    try {
      const allQuestions = questionSets.flatMap((set) =>
        Object.keys(set)
          .filter((k) => k.startsWith("questions"))
          .flatMap((k) => set[k])
      );

      const answersArray = Object.entries(answers).map(([questionId, data]) => {
        const questionContent =
          allQuestions.find((q) => q.questionId === questionId)?.content || "";
        return {
          questionId,
          content: questionContent,
          score: data.score || undefined,
          comment: data.comment,
          points: data.points,
        };
      });

      const response = await fetch(`${env.kataApiUrl}/api/accreditation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: "SADCAS F134-B",
          labId,
          section: "Process Requirements",
          auditorId: user._id,
          answers: answersArray,
        }),
      });

      if (!response.ok) throw new Error("Submission failed");
      fetchAccreditation();
      toast.success("Updated successfully!");
    } catch (error) {
      toast.error(error.message || "Failed to submit assessment");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isfetching) {
    return <div className={classes.loading}>Loading...</div>;
  }

  return (
    <div
      className={`${classes.wrapper} ${!editable ? classes.fromParams : ""}`}
    >
      <div className="d-flex justify-content-between align-items-center">
        <div
          className="d-flex align-items-center gap-2 mb-4"
          onClick={() => navigate(-1)}
          role="button"
        >
          <IoIosArrowRoundBack size={35} />
          <h2 className="m-0">Clause 7: Process Requirements</h2>
        </div>
        {userRole === "ASSESSOR" && !editable && (
          <CommonButton
            className={classes.editButton}
            onClick={() => setEditable(true)}
            title="Edit"
          />
        )}
      </div>

      {!editable && Object.keys(answers).length > 0 ? (
        <div className={classes.infoText}>
          ✅ Your SADCAS B - Clause 7: Process Requirements has been submitted.
        </div>
      ) : (
        questionSets[currentSet].render(handleAnswerChange, answers)
      )}

      {editable && (
        <CommonFooter
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          handleDone={handleDone}
          cancelText="Cancel"
          handleCancel={() => setEditable(false)}
          currentSet={currentSet}
          questionSets={questionSets}
          doneText={isSubmitting ? "Submitting..." : "Update"}
          disabled={isSubmitting}
        />
      )}
    </div>
  );
};

export default ProcessRequirements;
